import {
  BoxGeometry,
  CylinderGeometry,
  DodecahedronGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  SphereGeometry,
  TorusGeometry,
  Vector3,
} from 'three';
import { CITY_PALETTE, createBoardTexture, createShopSignTexture } from './theme.js';
import { addStaticCollider } from './util.js';
import { STATION_ACCESS, STATIONS } from './route.js';

/**
 * The two fuel stations from the map, both on the east side of Street 60:
 * Bashair (around s = 1.38 km) and Nabta (around s = 2.32 km). The canopy
 * proportions and walled forecourts are based on the Esri satellite layer;
 * the signage is reconstructed from the OSM names.
 */
export function buildStations(level, route, materials) {
  const setups = [];
  const forecourtMaterial = materials.concrete.clone();
  forecourtMaterial.map = materials.concrete.map.clone();
  forecourtMaterial.map.repeat.set(7, 12);
  forecourtMaterial.map.needsUpdate = true;

  for (const spec of STATIONS) {
    setups.push(buildStation(level, route, materials, forecourtMaterial, spec));
  }

  return setups;
}

function buildStation(level, route, materials, forecourtMaterial, spec) {
  const frame = route.frameAt(spec.s);
  const group = new Group();
  group.position.copy(frame.position);
  group.rotation.y = frame.yaw;
  level.root.add(group);

  const toWorld = (x, z) => {
    const point = frame.position
      .clone()
      .addScaledVector(frame.right, x)
      .addScaledVector(frame.tangent, -z);
    return point;
  };

  const addBox = (width, height, depth, x, y, z, material, { cast = true, receive = true } = {}) => {
    const mesh = new Mesh(new BoxGeometry(width, height, depth), material);
    mesh.position.set(x, y, z);
    mesh.castShadow = cast;
    mesh.receiveShadow = receive;
    group.add(mesh);
    return mesh;
  };

  const addCollider = (x, z, width, height, depth, y = height / 2) => {
    const point = toWorld(x, z);
    addStaticCollider(level, {
      x: point.x,
      y,
      z: point.z,
      width,
      height,
      depth,
      yaw: frame.yaw,
    });
  };

  // Forecourt and driveway aprons.
  addBox(33, 0.08, 60, 30.5, 0.04, 0, forecourtMaterial, { cast: false });
  for (const offset of STATION_ACCESS.drivewayOffsets) {
    addBox(
      3.6,
      0.05,
      STATION_ACCESS.drivewayWidth,
      12.4,
      0.067,
      offset,
      materials.asphalt,
      { cast: false },
    );
  }

  // Canopy.
  addBox(24, 0.62, 32, 29, 6.22, 0, materials.canopy);
  addBox(24.1, 0.18, 32.1, 29, 5.89, 0, materials.canopy, { cast: false });

  const fascia = new MeshStandardMaterial({
    color: spec.brand,
    roughness: 0.55,
    metalness: 0.12,
  });
  const accentMaterial = new MeshStandardMaterial({
    color: spec.accent,
    roughness: 0.5,
    metalness: 0.15,
  });
  addBox(24.2, 0.75, 0.32, 29, 6.02, 16.08, fascia);
  addBox(24.2, 0.75, 0.32, 29, 6.02, -16.08, fascia);
  addBox(0.32, 0.75, 32.2, 16.9, 6.02, 0, fascia);
  addBox(0.32, 0.75, 32.2, 41.1, 6.02, 0, fascia);
  addBox(24.25, 0.1, 0.34, 29, 5.62, 16.1, accentMaterial);
  addBox(24.25, 0.1, 0.34, 29, 5.62, -16.1, accentMaterial);

  // Six canopy columns.
  for (const x of [20.5, 37.5]) {
    for (const z of [-12, 0, 12]) {
      const column = new Mesh(new CylinderGeometry(0.3, 0.34, 5.9, 10), materials.whiteMetal);
      column.position.set(x, 2.95, z);
      column.castShadow = true;
      column.receiveShadow = true;
      group.add(column);
      addCollider(x, z, 0.7, 5.9, 0.7);
    }
  }

  // Three pump islands. The forecourt is deliberately a little chaotic.
  const islandMaterial = new MeshStandardMaterial({ color: 0xb8ad99, roughness: 0.86 });
  const pumpDark = new MeshStandardMaterial({ color: 0x263538, roughness: 0.55, metalness: 0.2 });
  const pumpLight = new MeshStandardMaterial({ color: 0xe9e3d4, roughness: 0.55 });
  for (const z of [-10, 0, 10]) {
    addBox(1.7, 0.22, 6.2, 29, 0.11, z, islandMaterial);
    for (const pumpZ of [z - 1.45, z + 1.45]) {
      addBox(0.82, 1.65, 0.58, 29, 0.95, pumpZ, pumpLight);
      addBox(0.5, 0.36, 0.05, 29, 1.35, pumpZ + 0.29, pumpDark);
      addBox(0.26, 0.5, 0.06, 29, 0.95, pumpZ + 0.3, pumpDark);
      addBox(0.82, 0.12, 0.62, 29, 1.82, pumpZ, fascia);
      addCollider(29, pumpZ, 0.9, 1.75, 0.7);
    }
    // A pair of fuel hoses, tied to the pump.
    for (const side of [-1, 1]) {
      const hose = new Mesh(
        new TorusGeometry(0.25, 0.035, 5, 10, Math.PI * 1.35),
        materials.tire,
      );
      hose.position.set(29 + side * 0.47, 0.84, z);
      hose.rotation.y = Math.PI / 2;
      group.add(hose);
    }
  }

  // Shop / office at the back of the plot.
  addBox(9, 4.2, 18, 42.5, 2.1, -17, materials.canopy);
  addBox(9.3, 0.22, 18.3, 42.5, 4.3, -17, materials.roof ?? materials.canopy);
  addBox(0.12, 1.3, 11.5, 37.98, 2.75, -17, materials.glass);
  addBox(0.12, 2.0, 1.8, 37.98, 1.15, -22.6, materials.glass);
  addBox(0.12, 1.6, 3.2, 37.98, 2.6, -13.7, materials.glass);
  addCollider(42.5, -17, 9, 4.2, 18);

  const shopSign = new Mesh(
    new PlaneGeometry(5.0, 1.05),
    new MeshBasicMaterial({
      map: createShopSignTexture('بقالة المحطة', {
        background: '#ece1c9',
        foreground: '#25322d',
        accent: spec.brand,
      }),
      toneMapped: false,
    }),
  );
  shopSign.position.set(37.9, 3.4, -17);
  shopSign.rotation.y = -Math.PI / 2;
  group.add(shopSign);

  for (let i = 0; i < 3; i += 1) {
    addBox(0.9, 0.55, 0.45, 37.5, 3.55 + i * 0.0, -10 - i * 1.8, materials.whiteMetal);
  }

  // Boundary walls at the back and sides; the street edge stays open.
  addBox(0.3, 2.4, 60, 47.15, 1.2, 0, materials.concrete);
  addBox(33, 2.4, 0.3, 30.6, 1.2, -30, materials.concrete);
  addBox(33, 2.4, 0.3, 30.6, 1.2, 30, materials.concrete);
  addCollider(47.15, 0, 0.3, 2.4, 60);
  addCollider(30.6, -30, 33, 2.4, 0.3);
  addCollider(30.6, 30, 33, 2.4, 0.3);

  // Canopy branding, facing the street.
  const canopySign = new Mesh(
    new PlaneGeometry(14.2, 1.25),
    new MeshBasicMaterial({
      map: createBoardTexture({
        width: 1400,
        height: 260,
        background: spec.brand,
        foreground: '#f8f4e7',
        accent: spec.accent,
        title: spec.nameArabic,
        titleLatin: spec.nameEnglish,
        subtitle: 'وقود · خدمة · FUEL',
        border: false,
        anisotropy: 8,
      }),
      toneMapped: false,
    }),
  );
  canopySign.position.set(16.72, 6.02, 0);
  canopySign.rotation.y = -Math.PI / 2;
  group.add(canopySign);

  // Tall price / name pylon near the south entrance, angled at the oncoming
  // northbound traffic.
  const pylonX = 15.8;
  const pylonZ = 23.8;
  addBox(0.22, 6.0, 0.22, pylonX - 1.4, 3.0, pylonZ, materials.metal);
  addBox(0.22, 6.0, 0.22, pylonX + 1.4, 3.0, pylonZ, materials.metal);
  const pylonMaterial = new MeshBasicMaterial({
    map: createBoardTexture({
      width: 900,
      height: 520,
      background: spec.brand,
      foreground: '#ffffff',
      accent: spec.accent,
      title: spec.nameArabic,
      titleLatin: spec.nameEnglish,
      subtitle: 'STREET 60 · الخرطوم',
      fuelIcon: true,
      border: true,
      anisotropy: 8,
    }),
    toneMapped: false,
  });
  const pylonSign = new Mesh(new PlaneGeometry(4.4, 2.5), pylonMaterial);
  pylonSign.position.set(pylonX, 6.0, pylonZ + 0.14);
  group.add(pylonSign);
  const pylonBack = new Mesh(new PlaneGeometry(4.4, 2.5), pylonMaterial);
  pylonBack.position.set(pylonX, 6.0, pylonZ - 0.14);
  pylonBack.rotation.y = Math.PI;
  group.add(pylonBack);
  addBox(4.8, 0.18, 0.32, pylonX, 7.33, pylonZ, fascia);
  addCollider(pylonX, pylonZ, 3.1, 6.0, 0.5);

  // Canopy downlights.
  for (const x of [23, 29, 35]) {
    for (const z of [-8, 8]) {
      addBox(0.55, 0.06, 1.8, x, 5.86, z, materials.canopyLight, { cast: false });
    }
  }

  // Yard clutter: oil drums, tyres, a bin.
  const drumColors = [0x8c3e2f, 0x2d5267, 0xb2924b];
  for (let i = 0; i < 5; i += 1) {
    const drum = new Mesh(
      new CylinderGeometry(0.4, 0.4, 0.9, 12),
      new MeshStandardMaterial({ color: drumColors[i % drumColors.length], roughness: 0.7, metalness: 0.2 }),
    );
    drum.position.set(44.5, 0.45, 18 + (i % 2) * 0.9);
    drum.castShadow = true;
    group.add(drum);
  }
  for (let i = 0; i < 4; i += 1) {
    const tyre = new Mesh(new TorusGeometry(0.34, 0.13, 6, 12), materials.tire);
    tyre.position.set(19 + i * 0.32, 0.13 + i * 0.25, 22);
    tyre.rotation.x = Math.PI / 2;
    tyre.castShadow = true;
    group.add(tyre);
  }

  addPerson(group, 44.2, 12.0, '#e7ddc9');
  addPerson(group, 44.8, -6.0, '#c9d1c5');
  addPerson(group, 33.0, 27.0, '#d9cbb3');

  addBackdrop(group, addBox, addCollider, materials);

  // The player may refuel anywhere under the canopy/forecourt. Keeping this
  // zone broad leaves room beside the scripted AI service lanes, which occupy
  // the pump islands themselves.
  const refuelZone = {
    minX: 15.5,
    maxX: 43,
    minZ: -22,
    maxZ: 22,
  };

  return { ...spec, frame, group, refuelZone };
}

/**
 * Buildings behind each filling station. The city-block builder deliberately
 * leaves a clear apron around the forecourts; without this backdrop the plot
 * would end in open desert, which reads as a hole in the neighbourhood.
 */
function addBackdrop(group, addBox, addCollider, materials) {
  const wallMaterials = CITY_PALETTE.house.map(
    (color) => new MeshStandardMaterial({ color, roughness: 0.94 }),
  );
  const roofMaterial = new MeshStandardMaterial({ color: CITY_PALETTE.roof[2], roughness: 0.97 });
  const windowMaterial = new MeshBasicMaterial({ color: 0x1b2528, toneMapped: false });
  const doorMaterial = new MeshStandardMaterial({ color: 0x4f3a27, roughness: 0.9 });
  const treeMaterial = new MeshStandardMaterial({ color: 0x586d43, roughness: 1 });

  const houses = [
    { x: 59, z: -44, w: 14, h: 4.8, d: 18, color: 0 },
    { x: 62, z: -20, w: 15, h: 3.9, d: 18, color: 2 },
    { x: 57.5, z: 5, w: 13, h: 5.5, d: 20, color: 3 },
    { x: 64, z: 31, w: 17, h: 4.2, d: 22, color: 1 },
    { x: 84, z: -33, w: 18, h: 6.4, d: 24, color: 4 },
    { x: 86, z: 8, w: 20, h: 5.2, d: 28, color: 5 },
    { x: 83, z: 48, w: 16, h: 4.6, d: 20, color: 2 },
  ];

  for (const house of houses) {
    const wall = wallMaterials[house.color % wallMaterials.length];
    addBox(house.w, house.h, house.d, house.x, house.h / 2, house.z, wall);
    addBox(house.w + 0.5, 0.22, house.d + 0.5, house.x, house.h + 0.11, house.z, roofMaterial);
    // A simple parapet around the roof, seen all over Khartoum.
    addBox(house.w + 0.5, 0.55, 0.18, house.x, house.h + 0.36, house.z - house.d / 2 - 0.15, wall);
    addBox(house.w + 0.5, 0.55, 0.18, house.x, house.h + 0.36, house.z + house.d / 2 + 0.15, wall);
    addBox(0.18, 0.55, house.d + 0.5, house.x - house.w / 2 - 0.15, house.h + 0.36, house.z, wall);
    addBox(0.18, 0.55, house.d + 0.5, house.x + house.w / 2 + 0.15, house.h + 0.36, house.z, wall);

    const frontX = house.x - house.w / 2 - 0.03;
    const windows = Math.max(2, Math.floor(house.d / 5));
    for (let i = 0; i < windows; i += 1) {
      const windowZ = house.z - house.d / 2 + 2.4 + i * (house.d - 4.8) / Math.max(1, windows - 1);
      const window = new Mesh(new PlaneGeometry(1.05, 1.3), windowMaterial);
      window.position.set(frontX, house.h * 0.58, windowZ);
      window.rotation.y = -Math.PI / 2;
      group.add(window);
    }

    const door = new Mesh(new BoxGeometry(0.12, 2.0, 1.3), doorMaterial);
    door.position.set(frontX - 0.02, 1.0, house.z);
    group.add(door);

    if (house.h > 4.5) {
      const tank = new Mesh(new CylinderGeometry(0.7, 0.7, 1.2, 10), materials.whiteMetal);
      tank.position.set(house.x + house.w * 0.22, house.h + 0.85, house.z + house.d * 0.22);
      tank.castShadow = true;
      group.add(tank);
    }

    addCollider(house.x, house.z, house.w, house.h, house.d);
  }

  for (const [x, z] of [
    [51.5, -56],
    [52, 56],
    [92, 38],
  ]) {
    const trunk = new Mesh(new CylinderGeometry(0.16, 0.24, 2.8, 6), materials.metal);
    trunk.position.set(x, 1.4, z);
    trunk.castShadow = true;
    group.add(trunk);
    const canopy = new Mesh(new DodecahedronGeometry(2.3, 0), treeMaterial);
    canopy.position.set(x, 3.9, z);
    canopy.scale.set(1.2, 0.9, 1.1);
    canopy.castShadow = true;
    group.add(canopy);
  }
}

function addPerson(group, x, z, robeColor) {
  const skin = new MeshStandardMaterial({ color: 0x6a4b36, roughness: 0.9 });
  const robe = new MeshStandardMaterial({ color: robeColor, roughness: 0.95 });
  const cap = new MeshStandardMaterial({ color: 0xe7e0d2, roughness: 0.9 });

  const body = new Mesh(new CylinderGeometry(0.22, 0.32, 1.15, 7), robe);
  body.position.set(x, 0.58, z);
  body.castShadow = true;
  group.add(body);
  const head = new Mesh(new SphereGeometry(0.13, 8, 6), skin);
  head.position.set(x, 1.29, z);
  head.castShadow = true;
  group.add(head);
  const capMesh = new Mesh(new CylinderGeometry(0.15, 0.15, 0.07, 8), cap);
  capMesh.position.set(x, 1.4, z);
  group.add(capMesh);
}
